import React from 'react';
import { Link } from 'react-router-dom';
import { X, ShoppingCart, ArrowRight, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCompare } from '../context/CompareContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ComparePage() {
  const { compareItems, removeFromCompare, clearCompare } = useCompare();
  const { addToCart } = useCart();
  const { user } = useAuth();

  const handleAddToCart = async (product) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }
    try {
      await addToCart(product.id, 0, 1);
      toast.success(`${product.name} added to cart`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (compareItems.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Scale className="h-20 w-20 mx-auto text-muted-foreground/50 mb-6" />
            <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-4">
              Compare Products
            </h1>
            <p className="text-muted-foreground mb-8">
              Add products to compare their features and prices
            </p>
            <Link to="/products">
              <Button className="bg-primary hover:bg-primary/90 rounded-full px-8 py-6 text-lg">
                Browse Products <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Get all unique attributes to compare
  const attributes = [
    { key: 'price', label: 'Starting Price' },
    { key: 'variants', label: 'Available Sizes' },
    { key: 'is_organic', label: 'Organic Certified' },
    { key: 'certifications', label: 'Certifications' },
    { key: 'stock', label: 'In Stock' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-stone-900">Compare Products</h1>
            <p className="text-muted-foreground">{compareItems.length} products selected</p>
          </div>
          <Button variant="outline" onClick={clearCompare} className="rounded-full">
            Clear All
          </Button>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className="p-4 text-left text-sm font-medium text-muted-foreground w-40">
                    Product
                  </th>
                  {compareItems.map((product) => (
                    <th key={product.id} className="p-4 text-center min-w-[200px]">
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute -top-2 right-0 h-6 w-6"
                          onClick={() => removeFromCompare(product.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Link to={`/products/${product.id}`}>
                          <div className="w-24 h-24 mx-auto rounded-xl overflow-hidden bg-stone-50 mb-3">
                            <img
                              src={product.images?.[0] || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200'}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="font-medium text-stone-900 line-clamp-2 hover:text-primary transition-colors">
                            {product.name}
                          </p>
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Price Row */}
                <tr className="border-b border-stone-100">
                  <td className="p-4 text-sm font-medium text-muted-foreground">Starting Price</td>
                  {compareItems.map((product) => (
                    <td key={product.id} className="p-4 text-center">
                      <span className="text-xl font-semibold text-primary">
                        ₹{product.variants?.[0]?.price || 0}
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Sizes Row */}
                <tr className="border-b border-stone-100">
                  <td className="p-4 text-sm font-medium text-muted-foreground">Available Sizes</td>
                  {compareItems.map((product) => (
                    <td key={product.id} className="p-4 text-center">
                      <div className="flex flex-wrap justify-center gap-1">
                        {product.variants?.map((v, i) => (
                          <span key={i} className="text-xs bg-stone-100 px-2 py-1 rounded-full">
                            {v.size}
                          </span>
                        ))}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Organic Row */}
                <tr className="border-b border-stone-100">
                  <td className="p-4 text-sm font-medium text-muted-foreground">Organic Certified</td>
                  {compareItems.map((product) => (
                    <td key={product.id} className="p-4 text-center">
                      {product.is_organic ? (
                        <span className="badge-organic">Yes</span>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Certifications Row */}
                <tr className="border-b border-stone-100">
                  <td className="p-4 text-sm font-medium text-muted-foreground">Certifications</td>
                  {compareItems.map((product) => (
                    <td key={product.id} className="p-4 text-center">
                      <div className="flex flex-wrap justify-center gap-1">
                        {product.certifications?.map((cert, i) => (
                          <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                            {cert}
                          </span>
                        ))}
                        {(!product.certifications || product.certifications.length === 0) && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Stock Row */}
                <tr className="border-b border-stone-100">
                  <td className="p-4 text-sm font-medium text-muted-foreground">Availability</td>
                  {compareItems.map((product) => {
                    const totalStock = product.variants?.reduce((sum, v) => sum + v.stock, 0) || 0;
                    return (
                      <td key={product.id} className="p-4 text-center">
                        {totalStock > 0 ? (
                          <span className="text-green-600 font-medium">In Stock ({totalStock})</span>
                        ) : (
                          <span className="text-red-600">Out of Stock</span>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Featured Row */}
                <tr className="border-b border-stone-100">
                  <td className="p-4 text-sm font-medium text-muted-foreground">Featured</td>
                  {compareItems.map((product) => (
                    <td key={product.id} className="p-4 text-center">
                      {product.is_featured ? (
                        <span className="badge-featured">Featured</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Add to Cart Row */}
                <tr>
                  <td className="p-4 text-sm font-medium text-muted-foreground">Action</td>
                  {compareItems.map((product) => (
                    <td key={product.id} className="p-4 text-center">
                      <Button
                        onClick={() => handleAddToCart(product)}
                        className="bg-primary hover:bg-primary/90 rounded-full"
                        data-testid={`compare-add-cart-${product.id}`}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
                      </Button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
